import React, { useMemo, useState } from 'react';
import { ArrowRight, Plane, Search } from 'lucide-react';
import SEO from './SEO';
import { SitePage, PageHero, CtaBand } from './site/ui';
import { inr, unsplash, waLink } from './site/brand';
import ServiceEnquiry from './site/ServiceEnquiry';
import { BACKEND_URL } from '../config';

const POPULAR = [
  { from: 'VGA', to: 'DXB', name: 'Vijayawada → Dubai' },
  { from: 'VGA', to: 'SIN', name: 'Vijayawada → Singapore' },
  { from: 'HYD', to: 'BKK', name: 'Hyderabad → Bangkok' },
  { from: 'HYD', to: 'KUL', name: 'Hyderabad → Kuala Lumpur' },
  { from: 'DEL', to: 'DPS', name: 'Delhi → Bali' },
  { from: 'BLR', to: 'MLE', name: 'Bengaluru → Malé' },
];

const fmtDuration = (d) => {
  const m = String(d || '').match(/PT(?:(\d+)H)?(?:(\d+)M)?/);
  if (!m) return d || '';
  return `${m[1] ? `${m[1]}h ` : ''}${m[2] ? `${m[2]}m` : ''}`.trim();
};
const mins = (d) => { const m = String(d || '').match(/PT(?:(\d+)H)?(?:(\d+)M)?/); return m ? (Number(m[1] || 0) * 60) + Number(m[2] || 0) : 0; };
const fmtTime = (s) => { const d = new Date(s); return Number.isNaN(d.getTime()) ? '' : d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }); };
const fmtDate = (s) => { const d = new Date(s); return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }); };

const FlightsPage = () => {
  const today = new Date().toISOString().split('T')[0];
  const [q, setQ] = useState({ origin: 'VGA', destination: '', departure_date: '', return_date: '', adults: 1, children: 0, travel_class: 'ECONOMY', non_stop: false, currency: 'INR' });
  const [flights, setFlights] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searched, setSearched] = useState(false);
  const [sortBy, setSortBy] = useState('price');

  const change = (e) => {
    const { name, value, type, checked } = e.target;
    let v = type === 'checkbox' ? checked : value;
    if (name === 'origin' || name === 'destination') v = v.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 3);
    if (name === 'adults' || name === 'children') v = Math.max(name === 'adults' ? 1 : 0, Number(v) || 0);
    setQ((p) => ({ ...p, [name]: v }));
  };

  const search = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setSearched(true);
    try {
      const body = { ...q, return_date: q.return_date || null };
      const res = await fetch(`${BACKEND_URL}/api/flights/search`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === 'string' ? data.detail : 'Search failed');
      setFlights(Array.isArray(data.results) ? data.results : []);
    } catch {
      setError('Live fares are unavailable right now. Send us your route below and we’ll quote the best fares by phone or WhatsApp.');
      setFlights([]);
    } finally {
      setLoading(false);
    }
  };

  const sorted = useMemo(() => [...flights].sort((a, b) => {
    if (sortBy === 'duration') return mins(a.duration) - mins(b.duration);
    if (sortBy === 'departure') return new Date(a.departure_time) - new Date(b.departure_time);
    return (Number(a.price) || 0) - (Number(b.price) || 0);
  }), [flights, sortBy]);

  const pax = (Number(q.adults) || 1) + (Number(q.children) || 0);
  const bookMsg = (f) => {
    const segs = f.itineraries?.[0]?.segments || [];
    const first = segs[0] || {}; const last = segs[segs.length - 1] || {};
    return `Hi VK Tours, please book: ${f.airline_name || f.airline} ${first.departure_airport || q.origin} → ${last.arrival_airport || q.destination} on ${fmtDate(f.departure_time)} ${fmtTime(f.departure_time)} for ${pax} traveller(s). Fare shown: ${inr(Number(f.price) || 0)} per person.`;
  };

  return (
    <SitePage>
      <SEO title="Flight Tickets · Domestic & International" description="Search domestic and international flights and book with VK Tours — fares quoted with or without a holiday package." path="/flights" />
      <PageHero
        image={unsplash('photo-1529947327457-8f5cb53bc1b6', 2000)}
        imageAlt="Aircraft wing above the clouds"
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Flights' }]}
        kicker="Air tickets"
        title="Book"
        accent="flights"
        lede="Domestic and international tickets for holidays, family visits and business — search fares, then book with our team."
        short
      />

      <section className="vk-section vk-section--sand" style={{ paddingTop: 32 }}>
        <div className="vk-wrap">
          <form className="vk-panel vk-flightform" onSubmit={search}>
            <div className="vk-flightform__grid">
              <div className="vk-field"><label htmlFor="fl-from">From *</label><input id="fl-from" name="origin" required value={q.origin} onChange={change} placeholder="Airport code, e.g. VGA" autoCapitalize="characters" /></div>
              <div className="vk-field"><label htmlFor="fl-to">To *</label><input id="fl-to" name="destination" required value={q.destination} onChange={change} placeholder="e.g. DXB, SIN" autoCapitalize="characters" /></div>
              <div className="vk-field"><label htmlFor="fl-dep">Departure *</label><input id="fl-dep" name="departure_date" type="date" required min={today} value={q.departure_date} onChange={change} /></div>
              <div className="vk-field"><label htmlFor="fl-ret">Return</label><input id="fl-ret" name="return_date" type="date" min={q.departure_date || today} value={q.return_date} onChange={change} /></div>
              <div className="vk-field"><label htmlFor="fl-ad">Adults</label><input id="fl-ad" name="adults" type="number" min="1" max="9" value={q.adults} onChange={change} /></div>
              <div className="vk-field"><label htmlFor="fl-ch">Children</label><input id="fl-ch" name="children" type="number" min="0" max="8" value={q.children} onChange={change} /></div>
              <div className="vk-field">
                <label htmlFor="fl-cls">Class</label>
                <select id="fl-cls" name="travel_class" value={q.travel_class} onChange={change}>
                  <option value="ECONOMY">Economy</option>
                  <option value="PREMIUM_ECONOMY">Premium economy</option>
                  <option value="BUSINESS">Business</option>
                  <option value="FIRST">First</option>
                </select>
              </div>
              <div className="vk-field vk-flightform__go">
                <label className="vk-check"><input type="checkbox" name="non_stop" checked={q.non_stop} onChange={change} /> Non-stop only</label>
                <button type="submit" className="vk-btn vk-btn--hot vk-btn--block" disabled={loading}><Search size={18} /> {loading ? 'Searching…' : 'Search flights'}</button>
              </div>
            </div>
          </form>

          {!searched ? (
            <div style={{ marginTop: 28 }}>
              <p className="vk-kicker">Popular routes</p>
              <div className="vk-chips">
                {POPULAR.map((r) => (
                  <button key={r.name} type="button" className="vk-chip" onClick={() => setQ((p) => ({ ...p, origin: r.from, destination: r.to }))}>{r.name}</button>
                ))}
              </div>
            </div>
          ) : null}

          {error ? <div className="vk-alert vk-alert--err" role="alert" style={{ marginTop: 20 }}>{error} <a href="#enquire" style={{ fontWeight: 700, textDecoration: 'underline' }}>Request a fare</a></div> : null}

          {loading ? <div className="vk-empty" style={{ marginTop: 20 }}><Plane size={32} /><p>Finding the best fares…</p></div> : null}

          {!loading && sorted.length ? (
            <>
              <div className="vk-flightbar">
                <p className="vk-form__note">{sorted.length} flights found · fares per person</p>
                <select className="vk-select" value={sortBy} onChange={(e) => setSortBy(e.target.value)} aria-label="Sort flights">
                  <option value="price">Cheapest first</option>
                  <option value="duration">Shortest first</option>
                  <option value="departure">Earliest departure</option>
                </select>
              </div>
              <div className="vk-flights">
                {sorted.map((f) => {
                  const segs = f.itineraries?.[0]?.segments || [];
                  const first = segs[0] || {}; const last = segs[segs.length - 1] || {};
                  const stops = Number(f.stops) || 0;
                  return (
                    <article key={f.id} className="vk-flight">
                      <div className="vk-flight__air"><b>{f.airline_name || f.airline}</b><small>{segs.map((s) => `${s.carrier_code}${s.flight_number}`).join(' · ')}</small></div>
                      <div className="vk-flight__route">
                        <div><b>{fmtTime(f.departure_time)}</b><small>{first.departure_airport} · {fmtDate(f.departure_time)}</small></div>
                        <div className="vk-flight__line"><small>{fmtDuration(f.duration)}</small><span /><small>{stops === 0 ? 'Non-stop' : `${stops} stop${stops > 1 ? 's' : ''}`}</small></div>
                        <div style={{ textAlign: 'right' }}><b>{fmtTime(f.arrival_time)}</b><small>{last.arrival_airport} · {fmtDate(f.arrival_time)}</small></div>
                      </div>
                      <div className="vk-flight__buy">
                        <strong>{inr(Number(f.price) || 0)}</strong>
                        <small>{pax > 1 ? `${inr((Number(f.price) || 0) * pax)} for ${pax}` : 'per person'}</small>
                        <a className="vk-btn vk-btn--hot vk-btn--sm" href={waLink(bookMsg(f))} target="_blank" rel="noopener noreferrer">Book <ArrowRight size={16} /></a>
                      </div>
                    </article>
                  );
                })}
              </div>
              <p className="vk-form__note" style={{ marginTop: 12 }}>Fares change quickly and are confirmed by our team at the time of booking.</p>
            </>
          ) : null}

          {!loading && searched && !error && !sorted.length ? (
            <div className="vk-empty" style={{ marginTop: 20 }}>
              <h3>No flights found</h3>
              <p>Try other dates or nearby airports — or ask us and we’ll check all airlines for you.</p>
              <a className="vk-btn vk-btn--hot" href="#enquire">Request a fare</a>
            </div>
          ) : null}
        </div>
      </section>

      <ServiceEnquiry
        title="Ask us for a fare"
        kicker="Fare request"
        productType="flight"
        subject="Hi VK Tours, I need a flight quote."
        defaults={{ from: q.origin }}
        fields={[
          { key: 'from', label: 'From', placeholder: 'City or airport', required: true },
          { key: 'to', label: 'To', placeholder: 'City or airport', required: true },
        ]}
        points={['Domestic and international airlines', 'Group fares for 10+ travellers', 'Add hotels, visa and insurance on the same booking']}
      />

      <CtaBand />
    </SitePage>
  );
};

export default FlightsPage;
